import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  ArrowForward,
  AutoStoriesOutlined,
  CheckCircle,
  CodeOutlined,
  EditOutlined,
  ForumOutlined,
  GroupsOutlined,
  PlayArrow,
  PsychologyOutlined,
  ScheduleOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const borderColor = "#E8ECF4";
const milestones = [
  {
    title: "Improve communication structure",
    description: "Practice clear and structured communication",
    status: "in-progress",
    icon: ForumOutlined,
  },
  {
    title: "Strengthen data structures and algorithms",
    description: "Build consistency in core DSA problem solving",
    status: "completed",
    icon: CodeOutlined,
  },
  {
    title: "Learn system design fundamentals",
    description: "Learn and practice system design concepts",
    status: "in-progress",
    icon: AutoStoriesOutlined,
  },
  {
    title: "Practice behavioral interviews",
    description: "Build concise stories for common behavioral questions",
    status: "upcoming",
    icon: GroupsOutlined,
  },
  {
    title: "Complete full mock interviews",
    description: "Take full-length interviews under realistic conditions",
    status: "upcoming",
    icon: PsychologyOutlined,
  },
];

const statusMeta = {
  completed: { label: "Completed", color: "success", dot: "#21C36F", background: "#ECFDF3" },
  "in-progress": { label: "In Progress", color: "primary", dot: "#4F6BFF", background: "#F3F5FF" },
  upcoming: { label: "Upcoming", color: "default", dot: "#C9D1E2", background: "#F8FAFC" },
};

export default function RoadmapPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("in-progress");
  const visibleMilestones = tab === "completed"
    ? milestones.filter((milestone) => milestone.status === "completed")
    : milestones;

  return (
    <Box sx={{ minHeight: "100%", backgroundColor: "#FFFFFF", py: { xs: 3, md: 5 } }}>
      <Container maxWidth="lg" sx={{ px: { xs: 2.5, sm: 4, lg: 6 } }}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "flex-start" }} spacing={2}>
          <Box>
            <Typography component="h1" variant="h3" sx={{ fontSize: { xs: "1.75rem", md: "2rem" }, letterSpacing: 0 }}>
              Your Roadmap
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.75 }}>
              A focused plan built around your interview goal.
            </Typography>
          </Box>
          <Button variant="outlined" startIcon={<EditOutlined />} onClick={() => navigate("/onboarding")} sx={{ borderRadius: 1.5, alignSelf: { xs: "stretch", sm: "flex-start" } }}>
            Edit Goals
          </Button>
        </Stack>

        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          aria-label="Roadmap milestone filter"
          sx={{ mt: 3, minHeight: 42, borderBottom: `1px solid ${borderColor}`, "& .MuiTab-root": { minHeight: 42, px: 2, textTransform: "none" } }}
        >
          <Tab value="in-progress" label="In Progress" />
          <Tab value="completed" label="Completed" />
        </Tabs>

        <Paper sx={{ mt: 2, border: `1px solid ${borderColor}`, borderRadius: 2, boxShadow: "none", overflow: "hidden" }}>
          <Box sx={{ p: { xs: 2.5, md: 3 }, backgroundColor: "#FAFBFF", borderBottom: `1px solid ${borderColor}` }}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1.5}>
              <Box>
                <Typography variant="caption" color="primary.main" sx={{ fontWeight: 700 }}>GOAL</Typography>
                <Typography component="h2" variant="h5" sx={{ mt: 0.5, letterSpacing: 0 }}>
                  Become interview-ready for a Software Engineer role
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                  <ScheduleOutlined sx={{ fontSize: 17, color: "text.secondary" }} />
                  <Typography variant="body2" color="text.secondary">Target date: August 15, 2026</Typography>
                </Stack>
              </Box>
              <Typography variant="h5" color="primary.main" sx={{ letterSpacing: 0 }}>60%</Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={60}
              aria-label="Roadmap progress: 60 percent"
              sx={{ mt: 2.5, height: 6, borderRadius: 999, backgroundColor: "#E9EDF5", "& .MuiLinearProgress-bar": { borderRadius: 999, backgroundColor: "#21C36F" } }}
            />
          </Box>

          <Box component="ol" sx={{ listStyle: "none", m: 0, p: { xs: 2, sm: 3 }, position: "relative" }}>
            {visibleMilestones.map((milestone, index) => {
              const meta = statusMeta[milestone.status];
              const Icon = milestone.icon;
              return (
                <Box
                  component="li"
                  key={milestone.title}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "36px minmax(0, 1fr)", sm: "44px minmax(0, 1fr) auto" },
                    columnGap: { xs: 1.5, sm: 2 },
                    alignItems: "center",
                    position: "relative",
                    minHeight: { xs: 112, sm: 92 },
                    py: 1,
                    borderBottom: index < visibleMilestones.length - 1 ? `1px solid ${borderColor}` : "none",
                  }}
                >
                  {index < visibleMilestones.length - 1 && (
                    <Box sx={{ position: "absolute", left: { xs: 17, sm: 21 }, top: "58%", bottom: "-42%", width: 2, backgroundColor: "#E3E8F2", zIndex: 0 }} />
                  )}
                  <Box sx={{ width: { xs: 36, sm: 44 }, height: { xs: 36, sm: 44 }, borderRadius: "50%", display: "grid", placeItems: "center", color: meta.dot, backgroundColor: meta.background, border: `1px solid ${meta.dot}30`, zIndex: 1 }}>
                    {milestone.status === "completed" ? <CheckCircle sx={{ fontSize: 21 }} /> : <Icon sx={{ fontSize: 21 }} />}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "text.primary" }}>{milestone.title}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>{milestone.description}</Typography>
                    <Chip
                      size="small"
                      label={meta.label}
                      color={meta.color}
                      variant={milestone.status === "upcoming" ? "outlined" : "filled"}
                      sx={{ display: { xs: "inline-flex", sm: "none" }, mt: 1.25, height: 24, color: meta.dot, backgroundColor: meta.background }}
                    />
                  </Box>
                  <Chip
                    size="small"
                    label={meta.label}
                    color={meta.color}
                    variant={milestone.status === "upcoming" ? "outlined" : "filled"}
                    sx={{ display: { xs: "none", sm: "inline-flex" }, height: 26, color: meta.dot, backgroundColor: meta.background }}
                  />
                </Box>
              );
            })}
            {visibleMilestones.length === 0 && (
              <Stack alignItems="center" textAlign="center" spacing={1} sx={{ py: 6 }}>
                <CheckCircle sx={{ color: "success.main", fontSize: 34 }} />
                <Typography sx={{ fontWeight: 700 }}>No completed milestones yet</Typography>
                <Typography variant="body2" color="text.secondary">Your completed work will be collected here.</Typography>
              </Stack>
            )}
          </Box>
        </Paper>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.3fr) minmax(240px, .7fr)" }, gap: 2, mt: 2 }}>
          <Paper sx={{ p: { xs: 2.5, md: 3 }, border: `1px solid ${borderColor}`, borderRadius: 2, boxShadow: "none", backgroundColor: "#F3F5FF" }}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={2}>
              <Box>
                <Typography variant="caption" color="primary.main" sx={{ fontWeight: 700 }}>RECOMMENDED EXERCISE</Typography>
                <Typography component="h2" variant="h6" sx={{ mt: 0.5 }}>Practice structured technical explanations</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Complete one focused interview and review the communication evidence.</Typography>
              </Box>
              <Button variant="contained" startIcon={<PlayArrow />} endIcon={<ArrowForward />} onClick={() => navigate("/interviews")} sx={{ borderRadius: 1.5, flexShrink: 0 }}>
                Start practice
              </Button>
            </Stack>
          </Paper>
          <Paper sx={{ p: 2.5, border: `1px solid ${borderColor}`, borderRadius: 2, boxShadow: "none" }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{ width: 42, height: 42, borderRadius: 1.5, display: "grid", placeItems: "center", color: "primary.main", backgroundColor: "#F3F5FF" }}>
                <ScheduleOutlined />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Expected commitment</Typography>
                <Typography sx={{ fontWeight: 700 }}>3 hours per week</Typography>
              </Box>
            </Stack>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
}
